/** The title bar and the status line: what is on the bench, and what the pointer or cursor is over. */

import { BLACK, BLUE, GREY, LIGHT_BLUE, WHITE, YELLOW } from '../../render/palette';
import type { Point } from '../model/grid';
import { cellAt } from '../model/grid';
import { elementName } from '../model/palette';
import { toolInfo } from '../model/shapes';
import type { KitApi } from './api';
import { HEADER, INK, STATUS } from './layout';
import { fit } from './widgets';

export function drawHeader(k: KitApi): void {
  const s = k.ui.screen;
  const { doc } = k.st;
  s.fill(HEADER.x, HEADER.y, HEADER.w, HEADER.h, BLUE);
  s.text('STONEFALL', 4, 2, WHITE);
  s.text('CONSTRUCTION KIT', 84, 2, LIGHT_BLUE);
  s.text(fit(doc.name || '(NO NAME)', 16), 240, 2, YELLOW);
  s.text(`${doc.grid.w}X${doc.grid.h}`, 380, 2, LIGHT_BLUE);
  const state = k.st.libraryKey === undefined
    ? { text: k.st.dirty ? 'NOT SAVED' : 'NEW', ink: k.st.dirty ? INK.warning : LIGHT_BLUE }
    : k.st.dirty ? { text: 'UNSAVED CHANGES', ink: INK.warning } : { text: 'SAVED', ink: INK.ok };
  s.text(state.text, 548, 2, state.ink, 'right');
  s.text('F1 HELP', 636, 2, LIGHT_BLUE, 'right');
}

/** The status-line hint for the cave view: what the buttons do, or the shape's size. */
function viewTip(k: KitApi): string {
  const stroke = k.painter.stroke;
  if (stroke && stroke.tool !== 'pencil') {
    const w = Math.abs(stroke.last.x - stroke.anchor.x) + 1;
    const h = Math.abs(stroke.last.y - stroke.anchor.y) + 1;
    const size = stroke.tool === 'line' ? `${Math.max(w, h)} CELLS` : `${w} X ${h}`;
    const keys = stroke.keyboard ? ': SPACE FINISHES, ESC CANCELS' : '';
    return `${toolInfo(stroke.tool).label} ${size} OF ${elementName(stroke.ch)}${keys}`;
  }
  const t = toolInfo(k.tool);
  if (k.tool === 'picker') return 'PICKER: CLICK A CELL TO SELECT ITS ELEMENT';
  if (k.tool === 'flood') return `FLOOD: LEFT FILLS WITH ${elementName(k.element)}, RIGHT WITH DIRT`;
  return `${t.label}: LEFT ${elementName(k.element)}, RIGHT DIRT, MIDDLE DRAG SCROLLS`;
}

/** `cell`: the cell under the pointer or the keyboard cursor, if any. `shown`: what that cell displays. */
export function drawStatus(k: KitApi, cell: Point | null, shown: string | undefined): void {
  const s = k.ui.screen;
  s.fill(STATUS.x, STATUS.y - 2, STATUS.w, STATUS.h + 2, BLACK);
  s.fill(STATUS.x, STATUS.y - 2, STATUS.w, 1, BLUE);
  let right = '';
  if (cell) {
    const ch = cellAt(k.st.doc.grid, cell.x, cell.y) ?? ' ';
    const what = ch === '?' && shown !== undefined && shown !== '?' ? `? ROLLS ${elementName(shown)}` : elementName(ch);
    right = `${String(cell.x).padStart(2, '0')},${String(cell.y).padStart(2, '0')} ${what}`;
  }
  s.text(right, STATUS.x + STATUS.w - 4, STATUS.y + 1, WHITE, 'right');
  const room = Math.floor((STATUS.w - 16 - right.length * 8) / 8);
  const msg = k.st.message;
  const hovered = k.ui.hovered;
  const tip = msg ? msg.text : hovered?.id === 'view' ? viewTip(k) : (hovered?.tip ?? '');
  s.text(fit(tip, room), STATUS.x + 4, STATUS.y + 1, msg ? msg.ink : hovered ? INK.text : GREY);
}
